/**
 * PCM Address Autocomplete
 *
 * "Begin typing your address" suggestions on any `.address-autocomplete`
 * input, via Google Places Autocomplete - the same widget and API key the
 * production theme binds in its scripts.js. Picking a suggestion fills the
 * street line and the city / state / ZIP fields of the same form.
 *
 * Companion fields are found inside the address input's own form, by class
 * first (`.city-autocomplete`, `.state-autocomplete`, `.zip-autocomplete`,
 * as the legacy popup templates mark them) and then by `name="city"` /
 * `"state"` / `"zip"`. Production filled every matching field on the page,
 * which wrote one form's address into any popup sitting behind it.
 *
 * The Maps script is gated on the "functional" consent category like
 * production, but only loads once a visitor focuses a field in a form that
 * has an address input, so pages where nobody touches a form don't pay for it.
 */
(function(window, document) {
  'use strict';

  const config = window.pcmAddressAutocomplete || {};
  const ADDRESS_SELECTOR = '.address-autocomplete';
  const SCRIPT_ID = 'pcm-google-maps-places';
  const READY_CALLBACK = 'pcmAddressAutocompleteReady';

  let requested = false;
  const boundInputs = [];

  function placesReady() {
    return !!(window.google && window.google.maps && window.google.maps.places);
  }

  function getComponent(place, types, useShortName) {
    const components = place.address_components || [];

    for (let t = 0; t < types.length; t++) {
      for (let i = 0; i < components.length; i++) {
        if (components[i].types.indexOf(types[t]) !== -1) {
          return useShortName ? components[i].short_name : components[i].long_name;
        }
      }
    }

    return '';
  }

  function findField(scope, role) {
    return scope.querySelector('.' + role + '-autocomplete') || scope.querySelector('[name="' + role + '"]');
  }

  // `skipInput` is for the address field itself: the widget treats any
  // `input` event on it as typing and fetches fresh suggestions, which land
  // after the pick has closed the dropdown and reopen it under a field that
  // no longer has focus - so nothing ever closes it again.
  function setValue(field, value, skipInput) {
    if (!field || !value) {
      return;
    }

    field.value = value;

    if (!skipInput) {
      field.dispatchEvent(new Event('input', { bubbles: true }));
    }

    field.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // A <select> stores the two-letter code ("FL") and so does a text input
  // capped at two characters; a plain text input gets the full name
  // ("Florida"), which is what production has always sent.
  function stateValue(field, place) {
    if (!field) {
      return '';
    }

    if (field.tagName === 'SELECT') {
      const code = getComponent(place, ['administrative_area_level_1'], true);
      const name = getComponent(place, ['administrative_area_level_1'], false);

      for (let i = 0; i < field.options.length; i++) {
        const option = field.options[i];

        if (option.value && (option.value === code || option.text === name)) {
          return option.value;
        }
      }

      return '';
    }

    const useShortName = field.maxLength > 0 && field.maxLength <= 2;

    return getComponent(place, ['administrative_area_level_1'], useShortName);
  }

  function fillAddress(input, place) {
    if (!place || !place.address_components) {
      return;
    }

    const scope = input.form || document;
    const street = [
      getComponent(place, ['street_number']),
      getComponent(place, ['route'])
    ].join(' ').trim();
    const stateField = findField(scope, 'state');

    setValue(input, street, true);
    setValue(findField(scope, 'city'), getComponent(place, ['locality', 'postal_town', 'sublocality_level_1', 'sublocality']));
    setValue(stateField, stateValue(stateField, place));
    setValue(findField(scope, 'zip'), getComponent(place, ['postal_code']));
  }

  function bind(input) {
    if (!placesReady() || input.dataset.pcmAddressAutocomplete === 'bound') {
      return;
    }

    input.dataset.pcmAddressAutocomplete = 'bound';
    input.dataset.pcmAddressPlaceholder = input.getAttribute('placeholder') || '';

    const autocomplete = new window.google.maps.places.Autocomplete(input, {
      types: ['address'],
      fields: ['address_components']
    });

    autocomplete.addListener('place_changed', function() {
      fillAddress(input, autocomplete.getPlace());
    });

    boundInputs.push(input);
    watchDropdowns();
  }

  // Browser autofill fills the address field while the visitor is in another
  // field (Company, Name) and fires `input` on it; the widget opens its
  // suggestions under the address field anyway and only closes them on that
  // field's blur, which has already happened. Suggestions belong on screen
  // only while an address field has focus, so hide any that open otherwise.
  function addressFieldFocused() {
    const active = document.activeElement;

    return !!(active && active.dataset && active.dataset.pcmAddressAutocomplete === 'bound');
  }

  const dropdownObserver = new MutationObserver(function(mutations) {
    if (addressFieldFocused()) {
      return;
    }

    mutations.forEach(function(mutation) {
      if (mutation.target.style.display !== 'none') {
        mutation.target.style.display = 'none';
      }
    });
  });

  function watchDropdown(dropdown) {
    if (dropdown.dataset.pcmAddressWatched) {
      return;
    }

    dropdown.dataset.pcmAddressWatched = '1';
    dropdownObserver.observe(dropdown, { attributes: true, attributeFilter: ['style'] });
  }

  let watchingBody = false;

  // Google appends one .pac-container to <body> per bound field, not always
  // synchronously, so watch for new ones as well as the ones already there.
  function watchDropdowns() {
    document.querySelectorAll('.pac-container').forEach(watchDropdown);

    if (watchingBody || !document.body) {
      return;
    }

    watchingBody = true;

    new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === 1 && node.classList.contains('pac-container')) {
            watchDropdown(node);
          }
        });
      });
    }).observe(document.body, { childList: true });
  }

  function bindAll() {
    document.querySelectorAll(ADDRESS_SELECTOR).forEach(bind);
  }

  function requestPlaces() {
    if (requested) {
      return;
    }

    requested = true;

    if (placesReady()) {
      bindAll();
      return;
    }

    if (!config.apiKey) {
      return;
    }

    const url = 'https://maps.googleapis.com/maps/api/js?key=' + encodeURIComponent(config.apiKey) + '&libraries=places&loading=async&callback=' + READY_CALLBACK;

    if (typeof window.pcmLoadExternalWhenAllowed === 'function') {
      window.pcmLoadExternalWhenAllowed('functional', url, 'script', SCRIPT_ID);
    }
  }

  function isVisible(element) {
    return window.getComputedStyle(element).display !== 'none' && element.querySelector('.pac-item') !== null;
  }

  window[READY_CALLBACK] = bindAll;

  // If Google rejects the key (e.g. a host the key's referrer list doesn't
  // cover), the widget disables the input and swaps the placeholder for
  // "Oops! Something went wrong." - hand the field back so the form still
  // works as a plain text input.
  const previousAuthFailure = window.gm_authFailure;

  window.gm_authFailure = function() {
    boundInputs.forEach(function(input) {
      input.disabled = false;
      input.style.backgroundImage = '';
      input.classList.remove('gm-err-autocomplete');
      input.setAttribute('placeholder', input.dataset.pcmAddressPlaceholder || '');
    });

    if (typeof previousAuthFailure === 'function') {
      previousAuthFailure();
    }
  };

  document.addEventListener('focusin', function(event) {
    const target = event.target;

    if (!target || typeof target.closest !== 'function') {
      return;
    }

    const isAddress = target.matches(ADDRESS_SELECTOR);

    if (!isAddress && !(target.form && target.form.querySelector(ADDRESS_SELECTOR))) {
      return;
    }

    requestPlaces();

    // Inputs rendered after the script loaded (popups, AJAX forms)
    if (isAddress) {
      bind(target);
    }
  });

  // Enter picks a suggestion from the dropdown - don't let it also submit the
  // form or advance a multistep form. Capture phase, so this runs before the
  // widget's own keydown handler closes the dropdown.
  document.addEventListener('keydown', function(event) {
    if (event.key !== 'Enter' || !event.target || event.target.dataset.pcmAddressAutocomplete !== 'bound') {
      return;
    }

    const dropdowns = document.querySelectorAll('.pac-container');

    for (let i = 0; i < dropdowns.length; i++) {
      if (isVisible(dropdowns[i])) {
        event.preventDefault();
        return;
      }
    }
  }, true);
})(window, document);
